import { useCallback, useEffect, useRef, useState, useMemo, type MouseEvent, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading, Link, LinkKind } from "@stubhub/broadway-web";
import type { MappingPagedCollectionOfIEventAndEventListGridItem } from "src/http/types";
import { Content, FormatContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { storeAnalyticsEvent } from "src/shared/utils/analyticsevent/analyticsevent-batcher";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { EventEntry, EventListCard } from "src/website-apps/shared/components/carousel/event-list-carousel";
import { ENTITY_TYPE } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { useStableNowState } from "src/website-apps/shared/hooks/useStableNowState";
import { WORLD_CUP_END_TIMESTAMP_MS } from "./constants";
import type { EventEntry as EventEntryType, Group, Event } from "./types";
import { getNextTwoMatchesPerGroup, getKnockoutRoundCards } from "./utils";
import { worldCupRecipes } from "./world-cup-widget.recipes";

const WORLD_CUP_CATEGORY_ID = 278_322;
const GROUP_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"] as const;
export interface EventListCarouselProps {
  carouselId?: string | number;
  categoryUrl?: string | undefined;
}

const WorldCupEventCard = ({
  group,
  index,
  isMobile,
}: {
  group: Group;
  index: number;
  isMobile: boolean;
}): ReactElement => {
  const appContext = useAppContext();
  const { env } = appContext;

  const handleClickOnGroupViewAllButton = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e?.stopPropagation?.();
      const { anonymousId, pageVisitId, sessionId, rawUserId, appVersion, cobrandId, clientApplicationId } = appContext;
      const analyticsEventPayload = {
        collectionId: "22",
        entityId: WORLD_CUP_CATEGORY_ID,
        entityTypeId: ENTITY_TYPE.Category,
        entityPosition: 1,
        index,
        categoryId: WORLD_CUP_CATEGORY_ID,
        anonymousId,
        pageVisitId,
        sessionId,
        userid: rawUserId,
        appVersion,
        cobrandId,
        clientApplicationId,
        worldCupGroup: group.groupLetter,
      };

      storeAnalyticsEvent(
        {
          applicationName: appContext.appName,
          applicationVersion: appVersion,
          eventName: "HomeCuratedContentClicks",
          eventBody: analyticsEventPayload,
        },
        env,
      );
    },
    [appContext, env, index, group],
  );

  const handleClickOnEvent = useCallback(
    (eventId: number) => (e: MouseEvent<Element>) => {
      e?.stopPropagation?.();
      const { anonymousId, pageVisitId, sessionId, rawUserId, appVersion, cobrandId, clientApplicationId } = appContext;
      const analyticsEventPayload = {
        collectionId: "22",
        entityId: eventId,
        entityTypeId: ENTITY_TYPE.Event,
        entityPosition: 1,
        index,
        categoryId: WORLD_CUP_CATEGORY_ID,
        eventId,
        anonymousId,
        pageVisitId,
        sessionId,
        userid: rawUserId,
        appVersion,
        cobrandId,
        clientApplicationId,
        worldCupGroup: group.groupLetter,
      };

      storeAnalyticsEvent(
        {
          applicationName: appContext.appName,
          applicationVersion: appVersion,
          eventName: "HomeCuratedContentClicks",
          eventBody: analyticsEventPayload,
        },
        env,
      );
    },
    [appContext, env, index, group],
  );

  //fire event when someone clicks view all
  return (
    <EventListCard<EventEntryType>
      header={<FormatContent id="cid$#4242208#" params={group.groupLetter} />}
      viewAllUrl={group.url}
      onViewAllClick={handleClickOnGroupViewAllButton}
      key={group.id}
      items={group.items}
      size={isMobile ? "mobile" : "default"}
      renderItem={(item) => <EventEntry key={item.id} onClick={handleClickOnEvent(item.id)} {...item} />}
    />
  );
};

export const WorldCupWidget = ({ carouselId, categoryUrl }: EventListCarouselProps): ReactElement | null => {
  const createQueryFn = useCreateQueryFn();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(0);
  const [mountedAt] = useStableNowState((now) => now.valueOf());
  const isTournamentOver = mountedAt >= WORLD_CUP_END_TIMESTAMP_MS;

  const {
    data,
    isLoading: pending,
    error,
  } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.WORLD_CUP_GROUP_MATCHES, { categoryId: WORLD_CUP_CATEGORY_ID }),
    queryFn: createQueryFn<MappingPagedCollectionOfIEventAndEventListGridItem>({
      method: "GetTeamMatches",
      params: [
        ["categoryId", WORLD_CUP_CATEGORY_ID],
        ["maxRows", 200],
      ],
      basePath: categoryUrl,
    }),
    staleTime: STALE_TIME_ONE_HOUR,
    enabled: !isTournamentOver,
  });

  const allItems = useMemo(() => (data?.items ?? []) as unknown as Event[], [data]);

  const bucketedGroups = useMemo<Event[][]>(() => {
    const buckets = new Map<string, Event[]>(GROUP_LETTERS.map((letter) => [letter, []]));
    for (const item of allItems) {
      const letter = item.eventMetadata?.sports?.tournamentEventMatchMetadata?.group;
      if (letter && buckets.has(letter)) {
        buckets.get(letter)!.push(item);
      }
    }
    return GROUP_LETTERS.map((letter) => buckets.get(letter) ?? []).filter((arr) => arr.length > 0);
  }, [allItems]);

  const groupCards = useMemo(
    () => getNextTwoMatchesPerGroup(bucketedGroups, categoryUrl, mountedAt),
    [bucketedGroups, categoryUrl, mountedAt],
  );
  const knockoutCards = useMemo(
    () => getKnockoutRoundCards(allItems, categoryUrl, mountedAt),
    [allItems, categoryUrl, mountedAt],
  );
  const allCards = useMemo(() => [...groupCards, ...knockoutCards], [groupCards, knockoutCards]);

  useEffect(() => {
    if (!carouselRef.current || allCards.length === 0) return;
    const firstCard = carouselRef.current.querySelector<HTMLElement>("[data-carousel-index='0']");
    if (firstCard) setCardHeight(firstCard.offsetHeight);
  }, [allCards]);

  const getArrowContainerHeight = useCallback(() => cardHeight, [cardHeight]);

  if (isTournamentOver) {
    return null;
  }

  if (pending) {
    return (
      <div className={worldCupRecipes.root}>
        <div className={worldCupRecipes.carousel}>
          <ResponsiveCarousel
            items={[]}
            isLoading
            loadingCount={isMobile ? 2 : 3}
            useFlex
            gapPx={24}
            hidePaginationButtons={isMobile}
            renderItem={() => null as never}
            classes={{ contentRoot: worldCupRecipes.carouselContentRoot }}
          />
        </div>
      </div>
    );
  }
  if (error || allCards.length === 0) {
    return null;
  }

  return (
    <div className={worldCupRecipes.root}>
      <div className={worldCupRecipes.header}>
        <Heading variant="header" size="medium">
          <Content id="cid$#4242193#" />
        </Heading>
        {categoryUrl && (
          <Link kind={LinkKind.Default} href={categoryUrl}>
            <Content id="cid$#4242189#" />
          </Link>
        )}
      </div>
      <div ref={carouselRef} className={worldCupRecipes.carousel}>
        <ResponsiveCarousel<Group>
          gapPx={24}
          useFlex
          items={allCards}
          renderItem={(group, index) => <WorldCupEventCard group={group} index={index} isMobile={isMobile} />}
          carouselId={carouselId}
          hidePaginationButtons={isMobile}
          getArrowContainerHeight={getArrowContainerHeight}
          classes={{ contentRoot: worldCupRecipes.carouselContentRoot }}
        />
      </div>
    </div>
  );
};
